import React, { useState, useRef, useEffect, useImperativeHandle } from 'react'
import { Calendar, Edit3, ChevronDown } from 'lucide-react'
import RichTextEditor from '../common/RichTextEditor'
import { getSession } from '../../getSession'
import { getUserFromHashedList } from '../../utils/lead'

const AddAppointment = React.forwardRef((props, ref) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    remarks: '',
    startDateTime: '',
    endDateTime: '',
    where: '',
    completeAddress: '',
    latitude: '',
    longitude: '',
    owner: '',
    collaborators: []
  })

  const [isCollaboratorsOpen, setIsCollaboratorsOpen] = useState(false)
  const collaboratorsRef = useRef(null)
  const startInputRef = useRef(null)
  const endInputRef = useRef(null)
  const startPickerRef = useRef(null)
  const endPickerRef = useRef(null)

  const [showStartPicker, setShowStartPicker] = useState(false)
  const [showEndPicker, setShowEndPicker] = useState(false)

  const [owners, setOwners] = useState([])
  const [collaboratorOptions, setCollaboratorOptions] = useState([])
  const [isUsersLoading, setIsUsersLoading] = useState(false)

  useEffect(() => {
    let mounted = true
    const fetchUsers = async () => {
      setIsUsersLoading(true)
      try {
        const { userId, TokenId } = getSession()
        const details = { UserId: userId || 0 }
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: details,
          BroadcastName: ''
        }
        const resp = await getUserFromHashedList(payload)
        let raw = resp?.Details ?? resp?.d ?? resp ?? []
        if (raw && raw.data) raw = raw.data
        // If API wraps array in another property try common names
        if (raw && !Array.isArray(raw)) {
          raw = raw.ResultList ?? raw.Users ?? raw.UserList ?? raw.Data ?? raw.Items ?? raw
        }
        const usersArray = Array.isArray(raw) ? raw : []
        const mapToOption = (u) => {
          const id = u.Code ?? u.Id ?? u.ID ?? u.UserId ?? u.Value ?? u.ValueId ?? ''
          const label = u.Text ?? u.TextN ?? u.Name ?? u.FullName ?? u.DisplayName ?? u.LoginName ?? String(id)
          return { value: String(id), label: String(label) }
        }
        if (mounted) {
          const options = usersArray.map(mapToOption)
          setOwners(options)
          setCollaboratorOptions(options)
        }
      } catch (error) {
        console.error('fetch users error:', error)
      } finally {
        if (mounted) setIsUsersLoading(false)
      }
    }
    fetchUsers()
    return () => { mounted = false }
  }, [])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const toggleCollaborator = (collaborator) => {
    setFormData(prev => ({
      ...prev,
      collaborators: prev.collaborators.includes(collaborator)
        ? prev.collaborators.filter(c => c !== collaborator)
        : [...prev.collaborators, collaborator]
    }))
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (collaboratorsRef.current && !collaboratorsRef.current.contains(event.target)) {
        setIsCollaboratorsOpen(false)
      }
      if (startPickerRef.current && !startPickerRef.current.contains(event.target)) {
        setShowStartPicker(false)
      }
      if (endPickerRef.current && !endPickerRef.current.contains(event.target)) {
        setShowEndPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      // collaborators already stored as array of values
      const collabIds = Array.isArray(formData.collaborators) ? formData.collaborators : [];
      return {
        Title: formData.title,
        Description: formData.description,
        Remarks: formData.remarks,
        StartDate: formData.startDateTime || null,
        EndDate: formData.endDateTime || null,
        Where: formData.where,
        CompleteAddress: formData.completeAddress,
        Latitude: formData.latitude || 0,
        Longitude: formData.longitude || 0,
        AssignedTo: formData.owner || 0,
        Collaborators: collabIds,
        IsTask: false
      }
    }
  }))

  // Google Places Autocomplete for 'Where' input
  const whereRef = useRef(null)

  useEffect(() => {
    const initAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) return
      if (!whereRef.current) return
      const autocomplete = new window.google.maps.places.Autocomplete(whereRef.current, { types: ['geocode'] })
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        const formatted = place.formatted_address || place.name || ''
        const lat = place.geometry && place.geometry.location ? place.geometry.location.lat() : ''
        const lng = place.geometry && place.geometry.location ? place.geometry.location.lng() : ''
        setFormData(prev => ({ ...prev, where: formatted, completeAddress: formatted, latitude: String(lat), longitude: String(lng) }))
      })
    }

    const isGoogleReady = () => window.google && window.google.maps && window.google.maps.places
    if (isGoogleReady()) {
      initAutocomplete()
      return
    }

    const scriptId = 'google-maps-script'
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script')
      script.id = scriptId
      script.type = 'text/javascript'
      script.src = 'https://maps.googleapis.com/maps/api/js?v=3&sensor=false&libraries=places&key=AIzaSyAO-dLMQIXYLvJeig0dbjeEqytvPg6EMd8'
      script.async = true
      script.defer = true
      script.onload = () => initAutocomplete()
      script.onerror = () => console.error('Google Maps script failed to load')
      document.body.appendChild(script)
    } else {
      const check = setInterval(() => {
        if (isGoogleReady()) {
          clearInterval(check)
          initAutocomplete()
        }
      }, 200)
      setTimeout(() => clearInterval(check), 10000)
    }
    // cleanup not necessary for google autocomplete listener
  }, [])

  const formatToDisplay = (val) => {
    if (!val) return ''
    const [datePart, timePart] = String(val).split('T')
    if (!datePart) return val
    const [year, month, day] = datePart.split('-').map(Number)
    const [hour = '00', minute = '00'] = (timePart || '').split(':')
    if (!year || !month || !day) return val
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const display = `${String(day).padStart(2, '0')}-${months[month - 1]}-${year} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    return display
  }

  const splitDateTime = (val) => {
    const [datePart = '', timePart = ''] = String(val || '').split('T')
    return { datePart, timePart: timePart.slice(0, 5) }
  }

  const mergeDateTime = (datePart, timePart) => {
    if (!datePart) return ''
    return `${datePart}T${timePart || '00:00'}`
  }

  const timeOptions = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      timeOptions.push(`${hh}:${mm}`)
    }
  }

  return (
    <div className="p-4 max-h-[70vh] overflow-y-auto">
      {/* Title */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="NewAppointment_20260106120931"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <div className="relative">
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Start Typing the details about the appointment"
            rows="3"
          />
          {/* <button className="absolute bottom-2 right-2 p-1.5 hover:bg-gray-100 rounded transition">
            <Edit3 className="w-4 h-4 text-gray-400" />
          </button> */}
        </div>
      </div>

      {/* Remarks */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Remarks
        </label>
        <div className="relative">
          <textarea
            value={formData.remarks}
            onChange={(e) => handleChange('remarks', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="about the task..."
            rows="3"
          />
          {/* <button className="absolute bottom-2 right-2 p-1.5 hover:bg-gray-100 rounded transition">
            <Edit3 className="w-4 h-4 text-gray-400" />
          </button> */}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Start Date and Time<span className="text-red-500">*</span>
        </label>
        <div className="relative" ref={startPickerRef}>
          <input
            type="text"
            readOnly
            value={formatToDisplay(formData.startDateTime)}
            onClick={() => { setShowStartPicker(!showStartPicker); setShowEndPicker(false); }}
            placeholder="Select date and time"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
          />
          <div
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={() => { setShowStartPicker(!showStartPicker); setShowEndPicker(false); }}
          >
            <Calendar className="w-4 h-4 text-gray-400" />
          </div>
          {showStartPicker && (
            <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 flex gap-2" style={{ minWidth: '280px' }}>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500 font-medium">Date</label>
                <input
                  type="date"
                  value={splitDateTime(formData.startDateTime).datePart}
                  onChange={(e) => handleChange('startDateTime', mergeDateTime(e.target.value, splitDateTime(formData.startDateTime).timePart))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500 font-medium">Time</label>
                <select
                  value={splitDateTime(formData.startDateTime).timePart}
                  onChange={(e) => handleChange('startDateTime', mergeDateTime(splitDateTime(formData.startDateTime).datePart, e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <button
                type="button"
                onClick={() => setShowStartPicker(false)}
                className="self-end px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
              >OK</button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          End Date and Time<span className="text-red-500">*</span>
        </label>
        <div className="relative" ref={endPickerRef}>
          <input
            type="text"
            readOnly
            value={formatToDisplay(formData.endDateTime)}
            onClick={() => { setShowEndPicker(!showEndPicker); setShowStartPicker(false); }}
            placeholder="Select end date and time"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
          />
          <div
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={() => { setShowEndPicker(!showEndPicker); setShowStartPicker(false); }}
          >
            <Calendar className="w-4 h-4 text-gray-400" />
          </div>
          {showEndPicker && (
            <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 flex gap-2" style={{ minWidth: '280px' }}>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500 font-medium">Date</label>
                <input
                  type="date"
                  value={splitDateTime(formData.endDateTime).datePart}
                  onChange={(e) => handleChange('endDateTime', mergeDateTime(e.target.value, splitDateTime(formData.endDateTime).timePart))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500 font-medium">Time</label>
                <select
                  value={splitDateTime(formData.endDateTime).timePart}
                  onChange={(e) => handleChange('endDateTime', mergeDateTime(splitDateTime(formData.endDateTime).datePart, e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <button
                type="button"
                onClick={() => setShowEndPicker(false)}
                className="self-end px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
              >OK</button>
            </div>
          )}
        </div>
      </div>

      {/* Where */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Where <span className="text-red-500">*</span>
        </label>
        <input
          ref={whereRef}
          type="text"
          value={formData.where}
          onChange={(e) => handleChange('where', e.target.value)}
          placeholder="Enter location of appointment"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Complete Address */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Complete Address
        </label>
        <textarea
          value={formData.completeAddress}
          onChange={(e) => handleChange('completeAddress', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          placeholder="Please enter complete address"
          rows="3"
        />
      </div>

      {/* Owner */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Owner
        </label>
        <select
          value={formData.owner}
          onChange={(e) => handleChange('owner', e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{isUsersLoading ? 'Loading...' : 'Select Owner'}</option>
          {owners.map((owner) => (
            <option key={owner.value} value={owner.value}>
              {owner.label}
            </option>
          ))}
        </select>
      </div>

      {/* Collaborators */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Collaborators
        </label>
        <div className="relative" ref={collaboratorsRef}>
          <button
            type="button"
            onClick={() => setIsCollaboratorsOpen(!isCollaboratorsOpen)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between bg-white"
          >
            <span className={formData.collaborators.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
              {formData.collaborators.length === 0
                ? 'Nothing selected'
                : `Selected: ${collaboratorOptions.filter(o => formData.collaborators.includes(o.value)).map(o => o.label).join(', ')}`}
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isCollaboratorsOpen ? 'rotate-180' : ''}`} />
          </button>

          {isCollaboratorsOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-blue-500 rounded shadow-lg max-h-48 overflow-y-auto">
              {collaboratorOptions.map((collaborator) => (
                <label
                  key={collaborator.value}
                  className={`flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer ${formData.collaborators.includes(collaborator.value) ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={formData.collaborators.includes(collaborator.value)}
                    onChange={() => toggleCollaborator(collaborator.value)}
                    className="mr-2 rounded"
                  />
                  <span className="text-sm">{collaborator.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
});

export default AddAppointment
